import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  ChangeDetectionStrategy,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  AfterViewInit,
  ElementRef,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { ReplaySubject, takeUntil } from 'rxjs';
import '@maersk-global/mds-components-core/mc-multi-select';
import '@maersk-global/mds-components-core/mc-input-date';
import { ToastrService } from '../../../../services/toastr-service';
import { ApplicationtTextService } from '../../../../services/application-text.service';
import { MaterialRateFilterForm } from '../../../../form-model/material-rate-filter.form.model';
import { MaterialRate } from '../../../../models/material-rate.model';
import { RegexConstants } from '../../../../RegexConstants';
import { SharedDataService } from '../../../../services/shared-data.service';
import { MasterDataManagementService } from '../../../../services/master-data-management.service';
import { LocationFilterComponent } from '../../../../shared-components/location-filter/location-filter.component';
import { DateFilterRowComponent } from '../../../../shared-components/date-filter-row/date-filter-row.component';
import { Shop } from '../../../../models/shop.model';
@Component({
  selector: 'app-material-rate-advanced-filters',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, RouterModule, LocationFilterComponent, DateFilterRowComponent],
  templateUrl: './material-rate-advanced-filters.component.html',
  styleUrls: ['./material-rate-advanced-filters.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class MaterialRateAdvancedFiltersComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('repairModeSelect', { static: false }) repairModeSelect!: ElementRef;
  @ViewChild('repairCodeSelect', { static: false }) repairCodeSelect!: ElementRef;
  @ViewChild(LocationFilterComponent) locationFilter?: LocationFilterComponent;
  @ViewChild('locationWrapper', { static: false }) locationWrapper?: ElementRef;
  /** Expiry date options for filtering */
  expiryDateTypes = [
    { value: 'EQUALS', label: 'Equals' },
    { value: 'ON_OR_BEFORE', label: 'On or Before' },
    { value: 'ON_OR_AFTER', label: 'On or After' },
    { value: 'BETWEEN', label: 'Between' },
    { value: 'BEFORE', label: 'Before' },
    { value: 'AFTER', label: 'After' },
  ];

  /** Effective date options for filtering */
  effectiveDateTypes = [
    { value: 'EQUALS', label: 'Equals' },
    { value: 'ON_OR_BEFORE', label: 'On or Before' },
    { value: 'ON_OR_AFTER', label: 'On or After' },
    { value: 'BETWEEN', label: 'Between' },
    { value: 'BEFORE', label: 'Before' },
    { value: 'AFTER', label: 'After' },
  ];

  /** Filter form passed from parent */
  @Input({ required: true }) materialRatesFilterForm!: FormGroup<MaterialRateFilterForm>;

  /** Material rate list data from parent (used to derive dropdown values) */
  @Input() materialRatesData: MaterialRate[] = [];
  @Output() materialRatesFilterApplied = new EventEmitter<MaterialRateFilterForm>();

  @Input() isGSCUser: boolean = false;
  @Input() allShops: Shop[] = [];

  /** Dropdown options */
  shopCodeOptions: string[] = [];
  repairModeOptions: { label: string; value: string }[] = [];
  repairCodeOptions: { label: string; value: string }[] = [];
  selectedRepairCodes: { label: string; value: string }[] = [];
  repairCodeTypeaheadData: { label: string; value: string }[] = [];
  repairModeTypeaheadData: { label: string; value: string }[] = [];
  selectedRepairModes: { label: string; value: string }[] = [];
  shopCodeRegex = RegexConstants.SHOP_CODE; // allows codes like “ABC”, “9KI”, “XYZ”
  repairCodeRegex = RegexConstants.REPAIR_CODE; // allows alphanumeric and special characters
  repairModeRegex = RegexConstants.REPAIR_MODE; // allows alphanumeric and special characters

  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  initialized: any;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: ToastrService,
    private readonly appTextService: ApplicationtTextService,
    private readonly sharedDataService: SharedDataService,
    private readonly masterDataManagementService: MasterDataManagementService,
  ) {}
  ngAfterViewInit() {
    this.repairModeSelect.nativeElement.addEventListener('clearbuttonclick', () => {
      this.onRepairModeCleared();
    });

    this.repairCodeSelect.nativeElement.addEventListener('clearbuttonclick', () => {
      this.onRepairCodeCleared();
    });

    if (this.locationWrapper) {
      for (const el of this.locationWrapper.nativeElement.querySelectorAll('mc-multi-select, mc-typeahead-multi-select')) {
        el.setAttribute('fit', 'medium');
      }
    }
  }

  ngOnInit(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.addControlsToForm();
    this.populateDropdownsFromData();
    this.fetchRepairModes();
    this.materialRatesFilterForm
      .get('repairMode')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((modes) => {
        this.selectedRepairModes = (modes || []).map((m: string) => ({ label: m, value: m }));
      });

    this.materialRatesFilterForm
      .get('repairCode')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((codes) => {
        this.selectedRepairCodes = (codes || []).map((c: string) => ({ label: c, value: c }));
      });

    const savedFilters = this.sharedDataService.getMaterialRateFilterFormValue();
    if (savedFilters) {
      this.restoreSavedSelections(savedFilters);
    }
  }

  /** If parent passes new data (e.g., on pagination/filter), rebuild dropdowns */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['materialRatesData'] && !changes['materialRatesData'].firstChange) {
      this.populateDropdownsFromData();
    }
  }
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
  /** Dynamically add form controls */
  private addControlsToForm() {
    this.materialRatesFilterForm.addControl('shopCode', this.formBuilder.control<string[]>([]));
    this.materialRatesFilterForm.addControl('repairMode', this.formBuilder.control<string[]>([]));
    this.materialRatesFilterForm.addControl('repairCode', this.formBuilder.control<string[]>([]));
    this.materialRatesFilterForm.addControl('expiryDateType', this.formBuilder.control<string | null>(null));
    this.materialRatesFilterForm.addControl('expiryDate', this.formBuilder.control<string | null>(null));
    this.materialRatesFilterForm.addControl('expiryDateFrom', this.formBuilder.control<string | null>(null));
    this.materialRatesFilterForm.addControl('expiryDateTo', this.formBuilder.control<string | null>(null));
    this.materialRatesFilterForm.addControl('effectiveDateType', this.formBuilder.control<string | null>(null));
    this.materialRatesFilterForm.addControl('effectiveDate', this.formBuilder.control<string | null>(null));
    this.materialRatesFilterForm.addControl('effectiveDateFrom', this.formBuilder.control<string | null>(null));
    this.materialRatesFilterForm.addControl('effectiveDateTo', this.formBuilder.control<string | null>(null));
    this.materialRatesFilterForm.addControl('regionCode', this.formBuilder.control<string[]>([]));
    this.materialRatesFilterForm.addControl('areaCode', this.formBuilder.control<string[]>([]));
    this.materialRatesFilterForm.addControl('countryCode', this.formBuilder.control<string[]>([]));
    this.materialRatesFilterForm.addControl('applyMaterialRatesFilter', this.formBuilder.control<boolean>(false));
  }
  private fetchRepairModes(): void {
    this.masterDataManagementService
      .getRepairModes()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (modes) => {
          this.repairModeOptions = modes;
          this.repairModeTypeaheadData = this.repairModeOptions;
        },
        error: () => {
          this.toastrService.showToastr('Failed to fetch repair modes.', 'error');
        },
      });
  }

  private populateDropdownsFromData(): void {
    if (!this.materialRatesData || this.materialRatesData.length === 0) {
      this.shopCodeOptions = [];
      return;
    }

    const uniqueShopCodes = Array.from(new Set(this.materialRatesData.map((item) => item.shopCode).filter(Boolean)));
    this.shopCodeOptions = uniqueShopCodes;
  }
  onRepairModeDropdownOpen(): void {
    if (this.repairModeOptions.length > 0) return; // prevent duplicate calls
    console.log('Fetching repair modes from API...');
    this.masterDataManagementService
      .getRepairModes()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (modes) => {
          this.repairModeOptions = modes;
        },
        error: () => {
          this.toastrService.showToastr('Failed to fetch repair modes.', 'error');
        },
      });
  }
  onRepairCodeDropdownOpen(): void {
    const selectedModes = this.materialRatesFilterForm.get('repairMode')?.value || [];
    // Always delegate to fetchRepairCodes()
    this.fetchRepairCodes(selectedModes);
  }
  private fetchRepairCodes(selectedModes: string[]): void {
    const fetch$ =
      selectedModes && selectedModes.length > 0
        ? this.masterDataManagementService.getRepairCodesByModes(selectedModes)
        : this.masterDataManagementService.getRepairCodes();
    fetch$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (codes) => {
        this.repairCodeOptions = codes;
        this.repairCodeTypeaheadData = this.repairCodeOptions;
        console.log('Fetched repair codes for modes', selectedModes, ':', codes);
      },
      error: () => {
        this.toastrService.showToastr('Failed to fetch repair codes for selected modes.', 'error');
        this.repairCodeOptions = [];
      },
    });
  }

  /** Apply filters */
  applyFilters(): void {
    this.materialRatesFilterForm.updateValueAndValidity();

    // Capture region, area/cluster, and country from location filter before applying
    if (this.locationFilter) {
      const locationFormGroup = this.locationFilter.getFormGroup();
      const regionCodes = locationFormGroup.controls['location'].value ?? [];
      const areaCodes = locationFormGroup.controls['countrycluster'].value ?? [];
      const countryCodes = locationFormGroup.controls['country'].value ?? [];
      const shopCodes = locationFormGroup.controls['shopCode'].value ?? [];

      this.materialRatesFilterForm.get('regionCode')?.setValue(regionCodes);
      this.materialRatesFilterForm.get('areaCode')?.setValue(areaCodes);
      this.materialRatesFilterForm.get('countryCode')?.setValue(countryCodes);
      this.materialRatesFilterForm.get('shopCode')?.setValue(shopCodes);
    }

    const rawFilters = this.materialRatesFilterForm.value;
    const expiryDateType = rawFilters.expiryDateType ?? '';
    // handle expiry date logic
    if (expiryDateType === 'BETWEEN') {
      if (!rawFilters.expiryDateFrom && !rawFilters.expiryDateTo) {
        this.toastrService.showToastr("Please select at least one date for 'Between' filter.", 'warning');
        return;
      }
    }
    // mark as applied
    this.sharedDataService.setMaterialRateFilterFormValue(rawFilters);
    this.materialRatesFilterForm.get('applyMaterialRatesFilter')?.setValue(true);
  }
  private restoreSavedSelections(savedFilters: any): void {
    if (savedFilters.repairMode && savedFilters.repairMode.length > 0) {
      this.selectedRepairModes = savedFilters.repairMode.map((mode: string) => ({
        label: mode,
        value: mode,
      }));

      this.materialRatesFilterForm.get('repairMode')?.setValue(savedFilters.repairMode);

      // Fetch dependent repair codes for these modes
      this.fetchRepairCodes(savedFilters.repairMode);
    }

    // 🟢 Restore repair codes
    if (savedFilters.repairCode && savedFilters.repairCode.length > 0) {
      this.selectedRepairCodes = savedFilters.repairCode.map((code: string) => ({
        label: code,
        value: code,
      }));

      this.materialRatesFilterForm.get('repairCode')?.setValue(savedFilters.repairCode);
    }

    // 🟢 Restore shop codes (already retained, but ensure consistent behavior)
    if (savedFilters.shopCode && savedFilters.shopCode.length > 0) {
      this.materialRatesFilterForm.get('shopCode')?.setValue(savedFilters.shopCode);
    }

    // 🟢 Restore region, area, and country codes
    if (savedFilters.regionCode && savedFilters.regionCode.length > 0) {
      this.materialRatesFilterForm.get('regionCode')?.setValue(savedFilters.regionCode);
    }
    if (savedFilters.areaCode && savedFilters.areaCode.length > 0) {
      this.materialRatesFilterForm.get('areaCode')?.setValue(savedFilters.areaCode);
    }
    if (savedFilters.countryCode && savedFilters.countryCode.length > 0) {
      this.materialRatesFilterForm.get('countryCode')?.setValue(savedFilters.countryCode);
    }

    // 🟢 Restore expiry filters
    this.materialRatesFilterForm.patchValue({
      expiryDateType: savedFilters.expiryDateType ?? null,
      expiryDate: savedFilters.expiryDate ?? null,
      expiryDateFrom: savedFilters.expiryDateFrom ?? null,
      expiryDateTo: savedFilters.expiryDateTo ?? null,
    });
    //  Restore effective date filters
    this.materialRatesFilterForm.patchValue({
      effectiveDateType: savedFilters.effectiveDateType ?? null,
      effectiveDate: savedFilters.effectiveDate ?? null,
      effectiveDateFrom: savedFilters.effectiveDateFrom ?? null,
      effectiveDateTo: savedFilters.effectiveDateTo ?? null,
    });
    if (savedFilters.repairMode?.length) {
      this.fetchRepairCodes(savedFilters.repairMode);
      setTimeout(() => {
        if (savedFilters.repairCode?.length) {
          this.selectedRepairCodes = savedFilters.repairCode.map((c: string) => ({ label: c, value: c }));
          this.materialRatesFilterForm.get('repairCode')?.setValue(savedFilters.repairCode);
        }
      }, 500); // give API a short window to complete
    }
  }

  /** Clear filters */
  clearFilters(): void {
    this.selectedRepairModes = [];
    this.selectedRepairCodes = [];

    // Reset location filter (Region, Cluster, Country, Shop Code)
    if (this.locationFilter) {
      this.locationFilter.getFormGroup().reset({
        location: [],
        countrycluster: [],
        country: [],
        shopCode: [],
      });
      this.locationFilter.countrycluster = [];
      this.locationFilter.countries = [];
      this.locationFilter.shopTypeaheadData = [];
      this.locationFilter.selectedShops = [];
    }

    // Reset form with new array references (not same object)
    this.materialRatesFilterForm.patchValue(
      {
        shopCode: [],
        repairMode: [],
        repairCode: [],
        expiryDateType: null,
        expiryDate: null,
        expiryDateFrom: null,
        expiryDateTo: null,
        effectiveDateType: null,
        effectiveDate: null,
        effectiveDateFrom: null,
        effectiveDateTo: null,
        regionCode: [],
        areaCode: [],
        countryCode: [],
      },
      { emitEvent: false },
    );
    this.sharedDataService.setMaterialRateFilterFormValue(null);

    // Mark as applied so parent updates
    //this.materialRatesFilterForm.get('applyMaterialRatesFilter')?.setValue(true);
  }

  /** Input validation utility */
  isControlInErrorState(control: FormControl): boolean | null {
    return control.invalid && control.touched ? true : null;
  }
  /** When user types in the search box */
  onRepairCodeSearch(event: any): void {
    const query = (event.detail?.value ?? '').toLowerCase();

    this.repairCodeTypeaheadData = this.repairCodeOptions
      .filter((opt) => opt.label.toLowerCase().includes(query) || opt.value.toLowerCase().includes(query))
      .map((opt) => ({ label: opt.label, value: opt.value }));
  }

  /** When user selects one or more options */
  onRepairCodeSelected(event: any): void {
    const customEvent = event as CustomEvent;
    const selectedItems = customEvent.detail?.value ?? customEvent.detail ?? [];

    this.selectedRepairCodes = selectedItems.map((s: any) => ({
      label: s.value ?? s,
      value: s.value ?? s,
    }));

    this.materialRatesFilterForm.get('repairCode')?.setValue(this.selectedRepairCodes.map((x) => x.value));
  }

  /** When user clears the field */

  onRepairCodeCleared() {
    this.selectedRepairCodes = [];
    this.materialRatesFilterForm.get('repairCode')?.setValue([]);
  }

  onRepairModeSearch(event: any): void {
    const query = (event.detail?.value ?? '').toLowerCase();
    this.repairModeTypeaheadData = this.repairModeOptions
      .filter((opt) => opt.label.toLowerCase().includes(query) || opt.value.toLowerCase().includes(query))
      .map((opt) => ({ label: opt.label, value: opt.value }));
  }

  onRepairModeSelected(event: any): void {
    const customEvent = event as CustomEvent;
    const selectedItems = customEvent.detail?.value ?? customEvent.detail ?? [];

    this.selectedRepairModes = selectedItems.map((s: any) => ({
      label: s.value ?? s,
      value: s.value ?? s,
    }));

    this.materialRatesFilterForm.get('repairMode')?.setValue(this.selectedRepairModes.map((x) => x.value));

    // Fetch dependent repair codes when modes change
    this.fetchRepairCodes(this.selectedRepairModes.map((x) => x.value));
  }

  onRepairModeCleared() {
    console.log('flow is here', this.selectedRepairModes);
    this.selectedRepairModes = [];
    this.materialRatesFilterForm.get('repairMode')?.setValue([]);
  }

  onShopCodesChange(shopCodes: string[]): void {
    this.materialRatesFilterForm.get('shopCode')?.setValue(shopCodes);
  }

  resetFiltersForm() {
    this.clearFilters();
  }
}
